import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { cargarPersonalEdificio, claveId } from "../../utils/personalEdificio";
import {
  FaClipboardList,
  FaCheck,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaFilter,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaReply,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, inputBase, pageContainer, pageTitle } from "../../utils/designTokens";

const TABS = [
  { key: "todas", label: "Todas" },
  { key: "pendiente", label: "Pendientes" },
  { key: "aprobada", label: "Aprobadas" },
  { key: "rechazada", label: "Rechazadas" },
];

// Colores por tipo de licencia
const TYPE_COLORS = {
  reglamentaria: { bg: colors.infoLight, color: colors.primaryDark },
  extraordinaria: { bg: colors.warningLight, color: colors.warningDark },
  estudio: { bg: colors.successLight, color: colors.successDark },
  maternidad_paternidad: { bg: colors.errorLight, color: colors.errorDark },
  sin_goce: { bg: colors.warningLight, color: colors.warningDark },
  enfermedad: { bg: colors.errorLight, color: colors.errorDark },
  otro: { bg: colors.gray100, color: colors.gray900 },
};

const SolicitudesAdmin = () => {
  const { selectedBuilding } = useBuilding();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("todas");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(""); // "aprobar" o "rechazar"
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [updating, setUpdating] = useState(false);

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (!buildingId) return;
    fetchEmployees();
  }, [buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    fetchRequests();
  }, [buildingId]);

  // Personal del edificio: employeebuilding por `building` + nombre de la
  // ficha users (utils/personalEdificio). Sin escanear la colección employee.
  const fetchEmployees = async () => {
    try {
      const personal = await cargarPersonalEdificio(buildingId);
      const empMap = {};
      personal.forEach((p) => {
        empMap[claveId(p.employeeid)] = p;
      });
      setEmployees(empMap);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees({});
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      // Tolerancia de tipo: intentar con numero y string
      const numId = Number(buildingId);
      let snap;
      try {
        const q = query(
          collection(db, "employee_requests"),
          where("buildingid", "==", numId)
        );
        snap = await getDocs(q);
      } catch (err) {
        console.error("Error fetching requests (number):", err);
      }
      if (!snap || snap.empty) {
        const q2 = query(
          collection(db, "employee_requests"),
          where("buildingid", "==", String(buildingId))
        );
        snap = await getDocs(q2);
      }
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setRequests(data);
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
    setLoading(false);
  };

  const openActionModal = (request, action) => {
    setSelectedRequest(request);
    setModalAction(action);
    setAdminResponse("");
    setShowModal(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;
    setUpdating(true);
    try {
      const newStatus = modalAction === "aprobar" ? "aprobada" : "rechazada";
      await updateDoc(doc(db, "employee_requests", selectedRequest.id), {
        status: newStatus,
        adminResponse: adminResponse.trim(),
        respondedBy: auth.currentUser?.email || "admin",
        updatedAt: Timestamp.now(),
      });
      setRequests((prev) =>
        prev.map((r) =>
          r.id === selectedRequest.id
            ? {
                ...r,
                status: newStatus,
                adminResponse: adminResponse.trim(),
                respondedBy: auth.currentUser?.email || "admin",
                updatedAt: Timestamp.now(),
              }
            : r
        )
      );
      setShowModal(false);
    } catch (err) {
      console.error("Error updating request:", err);
    }
    setUpdating(false);
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getEmployeeName = (empId, fallback) => {
    return employees[claveId(empId)]?.name || fallback || "Empleado";
  };

  const getTypeBadge = (type) => {
    const c = TYPE_COLORS[type] || TYPE_COLORS.otro;
    return (
      <span
        style={{
          background: c.bg,
          color: c.color,
          fontSize: typography.sizes.sm,
          fontWeight: 400,
          padding: "3px 10px",
          borderRadius: radius.md,
          textTransform: "capitalize",
        }}
      >
        {type || "Otro"}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    return <StatusBadge status={status || "pendiente"} />;
  };

  const filteredRequests = requests.filter((r) => {
    if (activeTab !== "todas" && r.status !== activeTab) return false;
    if (filterEmployee && claveId(r.employeeid) !== claveId(filterEmployee))
      return false;
    if (filterType && r.type !== filterType) return false;
    return true;
  });

  const employeeList = Object.entries(employees).map(([id, emp]) => ({
    id,
    name: emp.name || `Colaborador ${id}`,
  }));

  const requestTypes = [...new Set(requests.map((r) => r.type).filter(Boolean))];

  const counts = {
    todas: requests.length,
    pendiente: requests.filter((r) => r.status === "pendiente").length,
    aprobada: requests.filter((r) => r.status === "aprobada").length,
    rechazada: requests.filter((r) => r.status === "rechazada").length,
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaClipboardList}
          title="Seleccioná un edificio para ver las licencias"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Encabezado */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaClipboardList style={{ marginRight: 10, color: colors.primary }} />
          Licencias de Empleados
        </h1>
      </div>

      {/* Pestanas */}
      <div style={styles.tabsRow}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span
              style={{
                ...styles.tabCount,
                ...(activeTab === tab.key ? styles.tabCountActive : {}),
              }}
            >
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div style={styles.filtersRow}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Empleado</label>
          <select
            style={styles.select}
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
          >
            <option value="">Todos</option>
            {employeeList.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Tipo</label>
          <select
            style={styles.select}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Todos</option>
            {requestTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tarjetas de solicitudes */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: colors.gray600, marginTop: 12 }}>Cargando licencias...</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <EmptyState
          icon={FaSearch}
          title="No se encontraron licencias"
        />
      ) : (
        <div style={styles.cardList}>
          {filteredRequests.map((req) => (
            <div key={req.id} style={styles.requestCard}>
              <div style={styles.cardTop}>
                <div style={styles.cardTopLeft}>
                  <div style={styles.employeeRow}>
                    <FaUser style={{ color: colors.primary, marginRight: 6 }} />
                    <span style={{ fontWeight: 400, fontSize: 15 }}>
                      {getEmployeeName(req.employeeid, req.employeeName)}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {getTypeBadge(req.type)}
                    {renderStatusBadge(req.status)}
                  </div>
                </div>
                <div style={styles.cardTopRight}>
                  <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>
                    {formatDate(req.createdAt)}
                  </span>
                </div>
              </div>

              <div style={styles.cardBody}>
                <h3 style={styles.cardTitle}>{req.title || "Sin título"}</h3>
                {req.dateFrom && (
                  <p style={styles.cardDates}>
                    <FaCalendarAlt style={{ marginRight: 6, color: colors.gray600 }} />
                    {formatDate(req.dateFrom)} - {formatDate(req.dateTo)}
                  </p>
                )}
                {req.description && (
                  <p style={styles.cardDescription}>{req.description}</p>
                )}
                {/* Respuesta del admin */}
                {req.adminResponse && (
                  <div style={styles.responseBox}>
                    <FaReply style={{ color: colors.primary, marginRight: 6 }} />
                    <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>
                      {req.adminResponse}
                    </span>
                  </div>
                )}
              </div>

              {/* Acciones para pendientes */}
              {req.status === "pendiente" && (
                <div style={styles.cardActions}>
                  <button
                    style={styles.approveBtn}
                    onClick={() => openActionModal(req, "aprobar")}
                  >
                    <FaCheck style={{ marginRight: 6 }} /> Aprobar
                  </button>
                  <button
                    style={styles.rejectBtn}
                    onClick={() => openActionModal(req, "rechazar")}
                  >
                    <FaTimes style={{ marginRight: 6 }} /> Rechazar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de accion (aprobar/rechazar) */}
      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={modalAction === "aprobar" ? "Aprobar Licencia" : "Rechazar Licencia"}
        icon={modalAction === "aprobar" ? <FaCheckCircle style={{ color: colors.success }} /> : <FaTimesCircle style={{ color: colors.error }} />}
        subtitle={`${selectedRequest?.title || ""} - ${getEmployeeName(selectedRequest?.employeeid, selectedRequest?.employeeName)}`}
        size="narrow"
        footer={
          <>
            <button
              style={styles.cancelBtn}
              onClick={() => setShowModal(false)}
            >
              Cancelar
            </button>
            <button
              style={
                modalAction === "aprobar"
                  ? styles.confirmApproveBtn
                  : styles.confirmRejectBtn
              }
              onClick={handleAction}
              disabled={updating}
            >
              {updating
                ? "Procesando..."
                : modalAction === "aprobar"
                ? "Confirmar Aprobación"
                : "Confirmar Rechazo"}
            </button>
          </>
        }
      >
        <label style={styles.filterLabel}>
          Respuesta (opcional)
        </label>
        <textarea
          style={styles.textarea}
          rows={4}
          placeholder="Escribí un comentario o motivo..."
          value={adminResponse}
          onChange={(e) => setAdminResponse(e.target.value)}
        />
      </AdminModal>
    </div>
  );
};

// ─── Estilos con design tokens ───────────────────────────────────────────────
const styles = {
  container: {
    ...pageContainer,
  },
  header: {
    marginBottom: spacing[5],
  },
  title: {
    ...pageTitle,
    display: "flex",
    alignItems: "center",
  },
  tabsRow: {
    display: "flex",
    gap: 4,
    marginBottom: spacing[5],
    background: colors.gray100,
    borderRadius: radius.sm,
    padding: 4,
    border: `1px solid ${colors.gray200}`,
  },
  tab: {
    flex: 1,
    padding: "8px 16px",
    border: "none",
    borderRadius: 6,
    background: "transparent",
    cursor: "pointer",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    fontFamily: "inherit",
    transition: `all ${transitions.normal}`,
  },
  tabActive: {
    background: colors.primary,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0, 128, 255, 0.3)",
  },
  tabCount: {
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    background: "rgba(0,0,0,0.1)",
    color: "inherit",
    borderRadius: 10,
    padding: "2px 8px",
  },
  tabCountActive: {
    background: "rgba(255,255,255,0.25)",
    color: colors.white,
  },
  filtersRow: {
    display: "flex",
    gap: spacing[4],
    marginBottom: spacing[5],
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 180,
  },
  filterLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  select: {
    ...inputBase,
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  requestCard: {
    ...cardBase,
    overflow: "hidden",
    padding: 0,
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 20px 0",
  },
  cardTopLeft: {},
  cardTopRight: {},
  employeeRow: {
    display: "flex",
    alignItems: "center",
  },
  cardBody: {
    padding: "12px 20px",
  },
  cardTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px",
  },
  cardDates: {
    display: "flex",
    alignItems: "center",
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: "0 0 8px",
  },
  cardDescription: {
    fontSize: typography.sizes.md,
    color: colors.gray700,
    margin: "0 0 8px",
    lineHeight: 1.5,
  },
  responseBox: {
    display: "flex",
    alignItems: "flex-start",
    background: colors.primarySubtle,
    padding: "10px 14px",
    borderRadius: radius.sm,
    marginTop: spacing[2],
  },
  cardActions: {
    display: "flex",
    gap: 10,
    padding: "0 20px 16px",
  },
  approveBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    borderRadius: radius.sm,
    border: "none",
    background: colors.success,
    color: colors.white,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  rejectBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    borderRadius: radius.sm,
    border: "none",
    background: colors.error,
    color: colors.white,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  textarea: {
    ...inputBase,
    resize: "vertical",
    marginTop: 6,
    boxSizing: "border-box",
  },
  cancelBtn: {
    ...btnSecondary,
    fontFamily: typography.fontFamily,
  },
  confirmApproveBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    borderRadius: radius.sm,
    border: "none",
    background: colors.success,
    color: colors.white,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  confirmRejectBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    borderRadius: radius.sm,
    border: "none",
    background: colors.error,
    color: colors.white,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  spinner: {
    width: 36,
    height: 36,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default SolicitudesAdmin;
